import { LinkBox, SelectField } from '@flos/react-ui';
import { useHippoText } from '../../../../hooks/useHippoText';
import CoverableDetailsTemplate from '../../../../templates/CoverableDetailsTemplate';
import WizardPagetemplate from '../../../../templates/WizardPagetemplate';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import { type CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { useSaveCoverableInformation } from '../../../../hooks/useSaveCoverableInformation';
import { type ChangeEvent, useState } from 'react';
import CallMeModal from '../../../../shared_components/CallMeModal/CallMeModal';
import type { OptionProps } from '@flos/react-ui/build/core/forms/select/select-input';
import { isRequestPendingAtom } from '../../../../recoil/atoms/isRequestPendingAtom';
import { track } from '../../../../utils/trackingUtil';

const ContentAlreadyCoveredStep = ({
    goNext,
    goPrevious
}: {
    goNext: () => void;
    goPrevious: () => void;
}): JSX.Element => {
    const getHippoText = useHippoText();
    const isRequestPending = useRecoilValue(isRequestPendingAtom);
    const [coverables, setCoverables] = useRecoilState(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const { flavourName, fixedId } = currentProduct;
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const contentCoverables =
        flavourName === 'Indboforsikring'
            ? coverables.privateContent ?? []
            : coverables.youthContent ?? [];
    const coverableIndex = contentCoverables?.findIndex(
        (coverable) => coverable.fixedId === fixedId
    );
    const currentContentCoverable = contentCoverables[coverableIndex];
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [alreadyInsured, setAlreadyInsured] = useState<boolean | undefined>(
        currentContentCoverable.hasContentInsuranceSelectedReason?.hasInsurance
    );
    const [otherOptionSelected, setOtherOptionSelected] = useState<boolean>(false);
    const [showModal, setShowModal] = useState<boolean>(false);
    const renderReasonsSection = alreadyInsured !== undefined;

    const hasInsuranceNoOptions =
        currentContentCoverable.hasContentInsuranceOptions
            ?.filter((option) => option.hasInsurance === false)
            .map((option): OptionProps => {
                return {
                    label: option.name ?? '',
                    value: option.code ?? ''
                };
            }) ?? [];
    hasInsuranceNoOptions.push({ label: 'Andet', value: 'Other' });

    const hasInsuranceYesOptions =
        currentContentCoverable.hasContentInsuranceOptions
            ?.filter((option) => option.hasInsurance === true)
            .map((option): OptionProps => {
                return {
                    label: option.name ?? '',
                    value: option.code ?? ''
                };
            }) ?? [];
    hasInsuranceYesOptions.push({ label: 'Andet', value: 'Other' });

    const handleCovered = (answer: boolean): void => {
        if (answer === alreadyInsured) return;

        setAlreadyInsured(answer);

        const updatedCoverables = [...contentCoverables];
        updatedCoverables[coverableIndex] = {
            ...updatedCoverables[coverableIndex],
            hasContentInsuranceSelectedReason: undefined
        };

        setCoverables((prev) => ({
            ...prev,
            [flavourName === 'Indboforsikring' ? 'privateContent' : 'youthContent']:
                updatedCoverables
        }));
    };

    const setReason = (event: ChangeEvent<HTMLSelectElement>): void => {
        const { value } = event.target;

        const selectedReason = alreadyInsured
            ? hasInsuranceYesOptions.find((option) => option.value === value)
            : hasInsuranceNoOptions.find((option) => option.value === value);

        setOtherOptionSelected(selectedReason?.value === 'Other');

        const updatedCoverables = [...contentCoverables];
        updatedCoverables[coverableIndex] = {
            ...updatedCoverables[coverableIndex],
            hasContentInsuranceSelectedReason:
                currentContentCoverable.hasContentInsuranceOptions?.find(
                    (option) => option.code === value
                )
        };

        setCoverables((prev) => ({
            ...prev,
            [flavourName === 'Indboforsikring' ? 'privateContent' : 'youthContent']:
                updatedCoverables
        }));
    };

    const onNext = (): void => {
        if (
            otherOptionSelected ||
            (flavourName === 'Indboforsikring' &&
                currentContentCoverable.hasContentInsuranceSelectedReason?.code === 'NoOptedOut')
        ) {
            setShowModal(true);
            return;
        }
        setIsLoading(true);
        const updatedCoverable: CoverableInformationToUpdateDTO = {
            [flavourName === 'Indboforsikring' ? 'privateContent' : 'youngContent']: {
                publicId: currentContentCoverable.publicId,
                fixedId: currentContentCoverable.fixedId,
                HasInsuranceSelectedCode:
                    currentContentCoverable?.hasContentInsuranceSelectedReason?.code
            },
            productToUpdate: flavourName
        };

        void saveCoverableInformation(updatedCoverable).then(() => {
            const contentType =
                flavourName === 'Indboforsikring' ? 'privateContent' : 'youngContent';
            track(`${contentType}CoverableInformationAlreadyCoveredStep`, [
                {
                    HasInsuranceSelectedCode:
                        currentContentCoverable?.hasContentInsuranceSelectedReason?.code
                }
            ]);
            goNext();
            setIsLoading(false);
        });
    };

    return (
        <WizardPagetemplate
            onNext={onNext}
            onPrevious={goPrevious}
            disableNext={
                isLoading ||
                (currentContentCoverable.hasContentInsuranceSelectedReason === undefined &&
                    !otherOptionSelected) ||
                isRequestPending
            }
            disablePrevious={isLoading || isRequestPending}
            isNextLoading={isLoading || isRequestPending}
        >
            <CoverableDetailsTemplate
                header={getHippoText('DSS-COVERABLE-INFORMATION-STEP-ALREADY-COVERED-HEADER')}
            >
                <div className="u-display-flex u-flex-column u-flex-items-start u-gap-5">
                    <LinkBox
                        className={
                            isLoading || isRequestPending
                                ? 'u-color-supplement-grey-medium flos-box'
                                : 'flos-box'
                        }
                        title={getHippoText('DSS-COVERABLE-INFORMATION-STEP-ALREADY-COVERED-NO')}
                        onClick={() => {
                            if (isLoading || isRequestPending) return;
                            handleCovered(false);
                        }}
                        theme={!alreadyInsured ? 'highlight' : undefined}
                    ></LinkBox>
                    <LinkBox
                        className={
                            isLoading || isRequestPending
                                ? 'u-color-supplement-grey-medium flos-box'
                                : 'flos-box'
                        }
                        title={getHippoText('DSS-COVERABLE-INFORMATION-STEP-ALREADY-COVERED-YES')}
                        onClick={() => {
                            if (isLoading || isRequestPending) return;
                            handleCovered(true);
                        }}
                        theme={alreadyInsured ? 'highlight' : undefined}
                    ></LinkBox>
                    {renderReasonsSection &&
                        (alreadyInsured ? (
                            <div>
                                <h4>
                                    {getHippoText(
                                        'DSS-COVERABLE-INFORMATION-STEP-ALREADY-COVERED-HOW-ARE-YOU-COVERED'
                                    )}
                                </h4>
                                <SelectField
                                    label={getHippoText(
                                        'DSS-COVERABLE-INFORMATION-STEP-ALREADY-COVERED-DROPDOWN2'
                                    )}
                                    options={hasInsuranceYesOptions}
                                    onChange={setReason}
                                    value={
                                        hasInsuranceYesOptions.find(
                                            (option) =>
                                                option.value ===
                                                currentContentCoverable
                                                    .hasContentInsuranceSelectedReason?.code
                                        )?.value
                                    }
                                    disabled={isLoading || isRequestPending}
                                ></SelectField>
                            </div>
                        ) : (
                            <div>
                                <h4>
                                    {getHippoText(
                                        'DSS-COVERABLE-INFORMATION-STEP-ALREADY-COVERED-REASON-NOT-COVERED'
                                    )}
                                </h4>
                                <SelectField
                                    label={getHippoText(
                                        'DSS-COVERABLE-INFORMATION-STEP-ALREADY-COVERED-DROPDOWN1'
                                    )}
                                    options={hasInsuranceNoOptions}
                                    onChange={setReason}
                                    value={
                                        hasInsuranceNoOptions.find(
                                            (option) =>
                                                option.value ===
                                                currentContentCoverable
                                                    .hasContentInsuranceSelectedReason?.code
                                        )?.value
                                    }
                                    disabled={isLoading || isRequestPending}
                                ></SelectField>
                            </div>
                        ))}
                </div>
            </CoverableDetailsTemplate>
            {showModal && (
                <CallMeModal
                    title={getHippoText('DSS-CALL-ME-MODAL-NO-PRICE-HEADER')}
                    description={getHippoText('DSS-CALL-ME-MODAL-NO-PRICE-SUBHEADER')}
                    subdescription={getHippoText('DSS-CALL-ME-MODAL-NO-PRICE-BOOK-CALL-SUBHEADER')}
                    activityCode="dss_call_request"
                    showModal={showModal}
                    closeModal={() => {
                        setShowModal(false);
                    }}
                />
            )}
        </WizardPagetemplate>
    );
};

export default ContentAlreadyCoveredStep;
